import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type ChangeEvent,
} from 'react'
import { CalendarDays } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatIsoToRu, keepDateChars, normalizePastedDate, parseRuDate } from '@/lib/date-input'
import { Input } from './input'

/** Placeholder формата внутри поля — дословно из требований (`common.md` → «Поля даты», MR !10). */
export const DATE_PLACEHOLDER = '01.01.2000'

export interface DateInputProps {
  /** Значение в ISO `YYYY-MM-DD` — формат обмена с сервером; в поле показывается `дд.мм.гггг`. */
  value: string
  /** Вызывается с ISO-значением; пустая строка — только при полной очистке поля или по `blur`. */
  onChange: (iso: string) => void
  id?: string
  invalid?: boolean
  disabled?: boolean
  /** Границы диапазона в ISO — уходят в скрытое поле, чтобы календарь ограничивал выбор. */
  min?: string
  max?: string
  className?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
  'aria-label'?: string
  /**
   * Обязательность для вспомогательных технологий (`Field` отдаёт её в render-аргументах):
   * маркер «*» у подписи от них скрыт, поэтому состояние несёт сам ввод. Прокидывается на
   * ВИДИМОЕ текстовое поле; скрытый `input type="date"` для календаря остаётся без атрибутов
   * формы, как и раньше.
   */
  'aria-required'?: true | undefined
  /**
   * Незавершённый ввод при уходе с поля: по умолчанию — пустое значение наружу; `true` —
   * возвращает в поле прежнее значение и наружу ничего не отдаёт. Для полей, которые сохраняются
   * сразу (training-listener-completeness): недописанная дата не должна стирать сохранённую.
   */
  restoreOnIncompleteBlur?: boolean
}

/**
 * Поле ввода даты (centrika-docs, `common.md` → «Поля даты», MR !10).
 *
 * Видимое поле — обычный текстовый ввод: пользователь набирает `дд.мм.гггг` цифрами и точками,
 * маски нет, `Tab` уводит фокус с поля целиком, а не по частям даты. Календарь остаётся
 * браузерным: раздел «Выбор в календаре» требований не менялся, поэтому его поведение сохраняется —
 * иконка вызывает `showPicker()` скрытого `<input type="date">` (design D1).
 *
 * Наружу компонент отдаёт ISO. Набранный текст живёт во внутреннем состоянии: если выводить его из
 * `value` на каждом рендере, незавершённый ввод («28.0») затирался бы — то самое «ввод сбивается»,
 * на что жалуется заказчик. По той же причине пустое значение не эмитится во время набора: черновики
 * автосохраняются, и промежуточная пустая дата затёрла бы уже сохранённую (design D2).
 */
export function DateInput({
  value,
  onChange,
  id,
  invalid,
  disabled,
  min,
  max,
  className,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-required': ariaRequired,
  restoreOnIncompleteBlur = false,
}: DateInputProps) {
  const [text, setText] = useState(() => formatIsoToRu(value))
  /** Последнее значение, отданное наружу — чтобы отличать внешнее изменение от собственного эха. */
  const emitted = useRef(value)
  const inputRef = useRef<HTMLInputElement>(null)
  const pickerRef = useRef<HTMLInputElement>(null)
  /** Позиция каретки после фильтрации ввода: сама фильтрация не должна двигать курсор. */
  const caret = useRef<number | null>(null)

  // Внешнее изменение значения (загрузка черновика, сброс формы, выбор в календаре) пересобирает
  // текст. Собственное эхо игнорируем — иначе незавершённый ввод сбрасывался бы на каждом рендере.
  useEffect(() => {
    if (value === emitted.current) return
    emitted.current = value
    setText(formatIsoToRu(value))
  }, [value])

  useLayoutEffect(() => {
    if (caret.current === null || !inputRef.current) return
    inputRef.current.setSelectionRange(caret.current, caret.current)
    caret.current = null
  })

  function emit(iso: string) {
    emitted.current = iso
    onChange(iso)
  }

  function applyText(next: string, caretPos: number | null) {
    setText(next)
    caret.current = caretPos
    const iso = parseRuDate(next)
    if (iso) {
      emit(iso)
      return
    }
    // Полная очистка — сразу пустое значение; незавершённый ввод наружу не уходит (D2).
    if (next === '') emit('')
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value
    const next = keepDateChars(raw)
    const pos = e.target.selectionStart ?? next.length
    applyText(next, pos - (raw.length - next.length))
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text')
    if (!pasted) return
    e.preventDefault()
    const el = e.currentTarget
    const start = el.selectionStart ?? text.length
    const end = el.selectionEnd ?? start
    const merged = text.slice(0, start) + normalizePastedDate(pasted) + text.slice(end)
    const next = keepDateChars(merged)
    applyText(next, Math.min(next.length, start + normalizePastedDate(pasted).length))
  }

  function handleBlur() {
    if (parseRuDate(text)) return
    if (restoreOnIncompleteBlur && text !== '') {
      setText(formatIsoToRu(value))
      return
    }
    if (emitted.current !== '') emit('')
  }

  function openPicker() {
    const el = pickerRef.current
    if (!el) return
    // showPicker есть не везде и бросает на неизменяемом поле — тогда просто отдаём ему фокус,
    // дальше календарь открывает сам браузер.
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker()
        return
      } catch {
        /* fallthrough */
      }
    }
    el.focus()
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        placeholder={DATE_PLACEHOLDER}
        value={text}
        invalid={invalid}
        disabled={disabled}
        aria-invalid={ariaInvalid ?? invalid}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-required={ariaRequired}
        className={cn(!disabled && 'pr-10', className)}
        onChange={handleChange}
        onPaste={handlePaste}
        onBlur={handleBlur}
      />
      {!disabled ? (
        <>
          {/* Скрытое поле нужно только ради нативного календаря: непрозрачность 0, но ненулевой
                бокс в области иконки — иначе браузеру нечего якорить. Клики не ловит, в обход по
                Tab не попадает, значения формы не несёт (без name/required). */}
          <input
            ref={pickerRef}
            type="date"
            tabIndex={-1}
            aria-hidden="true"
            value={value}
            min={min}
            max={max}
            className="pointer-events-none absolute right-2 top-1/2 size-6 -translate-y-1/2 opacity-0"
            onChange={(e) => {
              const iso = e.target.value
              setText(formatIsoToRu(iso))
              emit(iso)
            }}
          />
          {/* Кнопка участвует в обходе по Tab: иначе календарь стал бы недоступен с клавиатуры —
                ухудшение раздела требований, которого изменение не касалось. */}
          <button
            type="button"
            aria-label="Открыть календарь"
            className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-7 text-txt-secondary outline-none hover:text-txt focus-visible:ring-2 focus-visible:ring-accent"
            onClick={openPicker}
          >
            <CalendarDays className="size-4 stroke-current" aria-hidden />
          </button>
        </>
      ) : null}
    </div>
  )
}
